import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import CartPanel from "@/components/CartPanel";
import OrderContextDialog from "@/components/OrderContextDialog";
import { useCart } from "@/contexts/CartContext";
import api from "@/lib/api";
import { Truck, ShoppingBag, MapPin, Clock, Loader2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";

const Order = () => {
  const navigate = useNavigate();
  const {
    items,
    orderType,
    postalCode,
    scheduledTime,
    scheduledDate,
    isNextDay,
    count,
    subtotal,
    itemsSubtotal,
    orderContextSet,
    setOrderContext,
    autoPromotions,
  } = useCart();

  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState(null);
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [ctxDialogOpen, setCtxDialogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([api.get("/products"), api.get("/settings")]);
        setProducts(p.data);
        setSettings(s.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Force user to set order context (Uber Eats style popup)
  useEffect(() => {
    if (!loading && !orderContextSet) {
      setCtxDialogOpen(true);
    }
  }, [loading, orderContextSet]);

  const categories = useMemo(() => {
    const cats = [...new Set(products.map((p) => p.category))];
    const adminOrder = Array.isArray(settings?.category_order) ? settings.category_order : [];
    if (adminOrder.length > 0) {
      const known = adminOrder.filter((c) => cats.includes(c));
      const extras = cats.filter((c) => !known.includes(c));
      return [...known, ...extras];
    }
    const fallback = ["Entrées", "Soupes Phở", "Bò Bún", "Plats", "Desserts", "Boissons"];
    return cats.sort((a, b) => fallback.indexOf(a) - fallback.indexOf(b));
  }, [products, settings]);

  useEffect(() => {
    if (!activeCat && categories.length) setActiveCat(categories[0]);
  }, [categories, activeCat]);

  const minOrder = useMemo(() => {
    if (!settings) return 20;
    const mins = settings.min_orders || {};
    if (postalCode && mins[postalCode]) return mins[postalCode];
    return settings.default_min_order ?? 20;
  }, [settings, postalCode]);

  const deliveryFee = useMemo(() => {
    if (!settings || orderType !== "delivery" || !postalCode) return 0;
    const fees = settings.delivery_fees || {};
    if (fees[postalCode] !== undefined) return fees[postalCode];
    return settings.default_delivery_fee ?? 8;
  }, [settings, postalCode, orderType]);

  const estimatedTime = useMemo(() => {
    if (!settings) return 35;
    const prep = settings.prep_time_minutes ?? 15;
    if (orderType === "pickup") return prep;
    const base = settings.base_delivery_time ?? 20;
    if (!postalCode) return prep + base;
    try {
      const diff = Math.abs(parseInt(postalCode) - 1018);
      return prep + base + Math.min(diff * 2, 30);
    } catch {
      return prep + base;
    }
  }, [settings, postalCode, orderType]);

  const grouped = useMemo(() => {
    const g = {};
    products.forEach((p) => {
      if (!g[p.category]) g[p.category] = [];
      g[p.category].push(p);
    });
    return g;
  }, [products]);

  const total = subtotal + (orderType === "delivery" ? deliveryFee : 0);
  const belowMin = orderType === "delivery" && itemsSubtotal < minOrder;

  const handleCheckout = () => {
    if (items.length === 0) return;
    navigate("/checkout");
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex flex-col">
      <Header />

      {/* Compact order context bar */}
      <section className="bg-white border-b border-neutral-200 sticky top-20 sm:top-24 md:top-28 z-30">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-2.5 md:py-3">
          <button
            data-testid="open-ctx-dialog"
            onClick={() => setCtxDialogOpen(true)}
            className="flex items-center gap-2 sm:gap-3 w-full text-left bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 rounded-md px-3 sm:px-4 py-2 sm:py-2.5 transition-colors overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
              {orderType === "delivery" ? (
                <Truck className="w-4 h-4 text-[#D90429]" />
              ) : (
                <ShoppingBag className="w-4 h-4 text-[#D90429]" />
              )}
              {orderType === "delivery" ? "Livraison" : "À emporter"}
            </span>
            {orderType === "delivery" && postalCode && (
              <span className="inline-flex items-center gap-1 text-xs text-neutral-600">
                <MapPin className="w-3.5 h-3.5" /> {postalCode}
              </span>
            )}
            {scheduledTime && (
              <span className="inline-flex items-center gap-1 text-xs text-neutral-600">
                <Clock className="w-3.5 h-3.5" />
                {scheduledTime === "asap" ? `Dès que possible · ~${estimatedTime} min` : scheduledTime}
              </span>
            )}
            {orderType === "delivery" && postalCode && minOrder > 0 && (
              <span
                data-testid="ctxbar-min-order"
                className="inline-flex items-center gap-1 text-xs font-bold text-neutral-700 bg-neutral-200 px-2 py-0.5 rounded"
                title={`Minimum de commande pour le code postal ${postalCode}`}
              >
                Min. {Number(minOrder).toFixed(2)} CHF
              </span>
            )}
            {isNextDay && scheduledDate && (
              <span
                data-testid="order-next-day-badge"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-black bg-[#E9C46A] text-black px-2 py-0.5 rounded"
              >
                Pour demain · {scheduledDate}
              </span>
            )}
            <Pencil className="w-3.5 h-3.5 text-neutral-400 ml-auto" />
          </button>
        </div>
      </section>

      {/* Active promotions banner */}
      {autoPromotions && autoPromotions.length > 0 && (
        <section data-testid="promo-banner" className="bg-[#D90429] text-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-2.5 text-xs sm:text-sm font-bold tracking-wide flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="uppercase tracking-[0.2em] text-[10px] sm:text-xs bg-white text-[#D90429] px-2 py-0.5 rounded">Promo</span>
            {autoPromotions.map((p, i) => (
              <span key={p.id || i} data-testid={`promo-banner-item-${i}`}>
                {p.name}
                {p.type === "global_percent" && ` — ${Number(p.value)}% sur toute la commande`}
                {p.type === "product_percent" && ` — ${Number(p.value)}% sur des produits sélectionnés`}
                {p.type === "product_fixed_price" && ` — prix réduit sur des produits`}
                {p.type === "product_amount_off" && ` — -${Number(p.value).toFixed(2)} CHF / unité`}
                {p.type === "bogo" && ` — 1 acheté = 1 offert`}
                {Number(p.min_subtotal) > 0 && ` (dès ${Number(p.min_subtotal).toFixed(2)} CHF)`}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Main content */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-5 md:px-8 lg:px-12 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Menu */}
        <div className="lg:col-span-8">
          {/* Category tabs - sticky below header + ctx bar */}
          {categories.length > 0 && (
            <div
              className="flex overflow-x-auto gap-2 mb-6 py-2 -mx-5 px-5 md:-mx-1 md:px-1 sticky top-[7.5rem] sm:top-[9rem] md:top-[10.5rem] bg-[#F9F9F7]/95 backdrop-blur-md z-30 border-b border-neutral-200/60 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              data-testid="category-tabs"
            >
              {categories.map((cat) => (
                <button
                  key={cat}
                  data-testid={`cat-tab-${cat}`}
                  onClick={() => {
                    setActiveCat(cat);
                    const el = document.getElementById(`cat-${cat}`);
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-semibold transition-colors border ${
                    activeCat === cat
                      ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                      : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-[#D90429]" />
            </div>
          ) : (
            <div className="space-y-10">
              {categories.map((cat) => (
                <div key={cat} id={`cat-${cat}`} className="scroll-mt-44">
                  <h2
                    className="text-2xl font-black mb-4 tracking-tight"
                    style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  >
                    {cat}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(grouped[cat] || [])
                      .filter((p) => p.available)
                      .map((p) => (
                        <ProductCard key={p.id} product={p} />
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cart - desktop */}
        <div className="hidden lg:block lg:col-span-4">
          <div className="sticky top-[11rem]">
            <CartPanel minOrder={minOrder} deliveryFee={deliveryFee} onCheckout={handleCheckout} />
          </div>
        </div>
      </div>

      {/* Sticky cart footer (mobile + desktop) */}
      {count > 0 && (
        <div
          data-testid="sticky-cart-footer"
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-neutral-200 px-5 py-3 shadow-2xl"
        >
          <Button
            data-testid="mobile-cart-button"
            onClick={() => setMobileCartOpen(true)}
            className="w-full bg-[#D90429] hover:bg-[#BF0424] text-white rounded-md h-12 font-bold flex items-center justify-between px-5"
          >
            <span className="flex items-center gap-2">
              <span className="bg-white/20 rounded-full w-6 h-6 flex items-center justify-center text-xs">
                {count}
              </span>
              <span>Voir le panier</span>
            </span>
            <span className="font-black">{total.toFixed(2)} CHF</span>
          </Button>
          {belowMin && (
            <p className="text-[11px] text-center text-[#BF0424] mt-1.5 font-bold">
              Min. {minOrder.toFixed(2)} CHF — ajoutez encore {(minOrder - subtotal).toFixed(2)} CHF
            </p>
          )}
        </div>
      )}

      {mobileCartOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/50"
          onClick={() => setMobileCartOpen(false)}
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-[#F9F9F7] rounded-t-2xl p-5 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-neutral-300 rounded-full mx-auto mb-4" />
            <CartPanel
              minOrder={minOrder}
              deliveryFee={deliveryFee}
              onCheckout={() => {
                setMobileCartOpen(false);
                handleCheckout();
              }}
            />
          </div>
        </div>
      )}

      <OrderContextDialog
        open={ctxDialogOpen}
        onClose={() => orderContextSet && setCtxDialogOpen(false)}
        initial={{ orderType, postalCode, scheduledTime, scheduledDate, isNextDay }}
        settings={settings}
        onConfirm={(payload) => {
          setOrderContext(payload);
          setCtxDialogOpen(false);
        }}
      />

      <Footer />
    </div>
  );
};

export default Order;
